import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

/**
 * GoogleCallback component handles the OAuth redirect from Google.
 * It extracts the authorization code from the URL and exchanges it for JWT tokens.
 */
const GoogleCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Processing Google login...');
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      // Handle error from Google
      if (errorParam) {
        setError(`Google login failed: ${errorParam}`);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // No authorization code
      if (!code) {
        setError('No authorization code received from Google');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        setStatus('Authenticating with server...');
        
        // Exchange the code for tokens
        const response = await api.post('/auth/google/callback/', { code });
        
        const { access, refresh, user } = response.data;

        // Store tokens and user data
        localStorage.setItem('access_token', access);
        localStorage.setItem('refresh_token', refresh);
        localStorage.setItem('user', JSON.stringify(user));

        setStatus('Login successful! Redirecting...');
        
        // Reload the page to reinitialize the auth context
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      } catch (err: any) {
        console.error('Google callback error:', err);
        const errorMsg = err.response?.data?.error || 
                        err.response?.data?.detail || 
                        'Failed to complete Google login';
        setError(errorMsg);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="container" style={{ maxWidth: '500px', marginTop: '4rem' }}>
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        {error ? (
          <>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
            <h2 style={{ color: '#e74c3c', marginBottom: '1rem' }}>Login Failed</h2>
            <div className="error">{error}</div>
            <p style={{ marginTop: '1rem', color: '#7f8c8d' }}>
              Redirecting to login page...
            </p>
          </>
        ) : (
          <>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
              <div className="spinner" style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
            </div>
            <h2 style={{ marginBottom: '1rem' }}>Signing you in...</h2>
            <p style={{ color: '#7f8c8d' }}>{status}</p>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default GoogleCallback;

